"use client";

import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ContentBlock } from '@/features/editor/utils/content-blocks';
import { ContentRenderer } from '@/features/content/components/ContentRenderer';
import { BlockEditor } from './BlockEditor';

interface SortableContentBlockProps {
  block: ContentBlock;
  onUpdate: (id: string, block: ContentBlock) => void;
  onDelete: (id: string) => void;
}

export function SortableContentBlock({ block, onUpdate, onDelete }: SortableContentBlockProps) {
  const [isEditing, setIsEditing] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id || '' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group border border-transparent hover:border-blue-300 rounded-xl transition-all ${
        isDragging ? 'opacity-50 scale-[1.01] shadow-md' : 'shadow-sm'
      }`}
    >
      {/* Drag Handle and Controls */}
      <div className="absolute -left-12 top-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          </svg>
        </div>
        
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          title={isEditing ? "Hide editor" : "Edit block"}
        >
          <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
        
        <button
          onClick={() => onDelete(block.id || '')}
          className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          title="Delete block"
        >
          <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Block Type Label */}
      <div className="absolute -top-2 left-4 bg-blue-600 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
        {block.type.replace('-', ' ').toUpperCase()}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-xl p-6">
        {isEditing ? (
          /* Edit Mode */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">
                Edit {block.type.replace('-', ' ')}
              </h4>
              <button
                onClick={() => setIsEditing(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Done
              </button>
            </div>
            <BlockEditor block={block} onUpdate={onUpdate} />
          </div>
        ) : (
          /* Preview Mode */
          <div className="pointer-events-none">
            <ContentRenderer blocks={[block]} />
          </div>
        )}
      </div>
    </div>
  );
}
